/**
 * OfflineIndicator.tsx - Visual indicator for offline status and data freshness
 *
 * Shows:
 * - Offline/Online status banner
 * - Data freshness (how old the cached data is)
 * - Pending sync items count
 */

import React, { useState, useEffect } from 'react';
import { useOfflineStatus } from '../../hooks/useOfflineStatus';
import './OfflineIndicator.css';

// Check if we're in E2E test mode - disable indicator to avoid blocking navigation
const isE2EMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (localStorage.getItem('e2e_test_mode') === 'true') return true;
  if (window.location.search.includes('e2e=true')) return true;
  return false;
};

export interface OfflineIndicatorProps {
  /** Position of the indicator */
  position?: 'top' | 'bottom';
  /** Whether to show when online (for data freshness) */
  showWhenOnline?: boolean;
  /** Threshold in ms to show stale data warning (default 5 min) */
  staleThreshold?: number;
  /** Custom class name */
  className?: string;
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  position = 'top',
  showWhenOnline = false,
  staleThreshold = 5 * 60 * 1000, // 5 minutes
  className = '',
}) => {
  // Check E2E mode at component initialization
  const [isE2E] = useState(() => isE2EMode());

  const {
    isOnline,
    isConnected,
    isChecking,
    lastSyncTime,
    dataAgeFormatted,
    pendingActionsCount,
  } = useOfflineStatus();

  // In E2E test mode, always return null to avoid blocking navigation clicks
  if (isE2E) return null;

  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Determine if we should show the indicator
  useEffect(() => {
    if (isDismissed) {
      setIsVisible(false);
      return;
    }

    // Always show when offline
    if (!isOnline || !isConnected) {
      setIsVisible(true);
      return;
    }

    // Show when online with stale data or pending actions
    if (showWhenOnline) {
      const dataAge = lastSyncTime > 0 ? Date.now() - lastSyncTime : 0;
      const hasStaleData = dataAge > staleThreshold;
      const hasPendingActions = pendingActionsCount > 0;

      setIsVisible(hasStaleData || hasPendingActions);
      return;
    }

    setIsVisible(false);
  }, [isOnline, isConnected, lastSyncTime, pendingActionsCount, showWhenOnline, staleThreshold, isDismissed]);

  // Reset dismissed state when going offline
  useEffect(() => {
    if (!isOnline) {
      setIsDismissed(false);
    }
  }, [isOnline]);

  if (!isVisible) return null;

  const getStatusType = (): 'offline' | 'reconnecting' | 'stale' | 'syncing' => {
    if (!isOnline) return 'offline';
    if (isChecking) return 'reconnecting';
    if (!isConnected) return 'reconnecting';
    if (pendingActionsCount > 0) return 'syncing';
    return 'stale';
  };

  const statusType = getStatusType();

  const getStatusMessage = (): string => {
    switch (statusType) {
      case 'offline':
        return 'You are offline';
      case 'reconnecting':
        return 'Reconnecting...';
      case 'syncing':
        return `${pendingActionsCount} action${pendingActionsCount !== 1 ? 's' : ''} pending sync`;
      case 'stale':
        return 'Showing cached data';
      default:
        return '';
    }
  };

  const getStatusIcon = (): string => {
    switch (statusType) {
      case 'offline':
        return 'cloud_off';
      case 'reconnecting':
        return 'sync';
      case 'syncing':
        return 'sync';
      case 'stale':
        return 'schedule';
      default:
        return 'info';
    }
  };

  const handleDismiss = () => {
    // Only allow dismissing when online with stale data
    if (isOnline && isConnected) {
      setIsDismissed(true);
    }
  };

  return (
    <div
      className={`offline-indicator-banner offline-indicator-${position} offline-indicator-${statusType} ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="offline-indicator-content">
        <span className="offline-indicator-icon material-icons" aria-hidden="true">
          {getStatusIcon()}
        </span>
        <div className="offline-indicator-text">
          <span className="offline-indicator-status">{getStatusMessage()}</span>
          {lastSyncTime > 0 && (
            <span className="offline-indicator-freshness">
              Last synced: {dataAgeFormatted}
            </span>
          )}
        </div>
        {isOnline && isConnected && (
          <button
            className="offline-indicator-dismiss"
            onClick={handleDismiss}
            aria-label="Dismiss notification"
          >
            <span className="material-icons" aria-hidden="true">close</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default OfflineIndicator;
